import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FilmStrip, CheckCircle, XCircle, Clock, ArrowRight } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { hapticSuccess } from '../lib/native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_POLLS = 6;
const POLL_INTERVAL_MS = 2000;

const PaymentSuccess = ({ user }) => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ status: 'polling', data: null, message: '' });
  const pollCountRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: 'error', data: null, message: 'Missing session id' });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${BACKEND_URL}/api/payments/status/${sessionId}`);
        if (cancelled) return;
        const data = res.data;
        if (data.payment_status === 'paid') {
          hapticSuccess();
          setState({ status: 'success', data, message: '' });
          return;
        }
        if (data.payment_status === 'failed' || data.payment_status === 'expired') {
          setState({ status: 'failed', data, message: `Payment ${data.payment_status}` });
          return;
        }
        pollCountRef.current += 1;
        if (pollCountRef.current >= MAX_POLLS) {
          setState({
            status: 'timeout',
            data,
            message: 'Still processing. Check your transactions page in a minute.',
          });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setState({
          status: 'error',
          data: null,
          message: e.response?.data?.detail || 'Could not verify payment',
        });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const dashboardRoute = () => {
    if (user?.role === 'admin') return '/admin-dashboard';
    if (user?.role === 'producer') return '/producer-dashboard';
    if (user?.role === 'fan') return '/fan-dashboard';
    return '/investor-dashboard';
  };

  const renderBody = () => {
    if (state.status === 'polling') {
      return (
        <>
          <div aria-hidden="true" className="w-16 h-16 mx-auto mb-6 rounded-full border-2 border-gold border-t-transparent animate-spin" />
          <h1 className="font-heading text-3xl font-bold mb-3">VERIFYING <span className="text-gold">PAYMENT</span></h1>
          <p className="font-body text-slate-400">Talking to Stripe, hold tight…</p>
        </>
      );
    }
    if (state.status === 'success') {
      const isPledge = state.data?.kind === 'fan_pledge';
      return (
        <>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mb-5">
            <CheckCircle size={84} weight="fill" className="text-gold mx-auto" />
          </motion.div>
          <h1 className="font-heading text-3xl font-bold mb-3">
            {isPledge ? 'BACKING ' : 'INVESTMENT '}
            <span className="text-gold">CONFIRMED</span>
          </h1>
          <p className="font-body text-slate-400 mb-2">
            ₹{state.data.amount?.toLocaleString('en-IN')} {state.data.currency?.toUpperCase()} charged successfully.
          </p>
          {!isPledge && (
            <div className="bg-gold/5 border border-gold/30 rounded-md p-4 my-6 text-left">
              <div className="text-xs uppercase tracking-wide text-gold font-bold mb-1">Escrow Status</div>
              <p className="font-body text-sm text-slate-300">
                Your investment is held in <span className="text-gold font-bold">escrow</span> until
                the movie reaches its funding goal. If the goal isn&apos;t met, your investment will be
                refunded automatically.
              </p>
            </div>
          )}
          <div className="flex gap-3 justify-center mt-6">
            <button
              data-testid="success-dashboard-btn"
              onClick={() => navigate(dashboardRoute())}
              className="btn-primary flex items-center gap-2"
            >
              Go to Dashboard <ArrowRight size={16} weight="bold" />
            </button>
            <button
              data-testid="success-movie-btn"
              onClick={() => navigate(`/movie/${state.data.movie_id}`)}
              className="btn-secondary"
            >
              Back to Movie
            </button>
          </div>
        </>
      );
    }
    if (state.status === 'failed') {
      return (
        <>
          <XCircle size={84} weight="fill" className="text-red-400 mx-auto mb-5" />
          <h1 className="font-heading text-3xl font-bold mb-3">
            PAYMENT <span className="text-red-400">{state.data?.payment_status?.toUpperCase()}</span>
          </h1>
          <p className="font-body text-slate-400 mb-6">{state.message}</p>
          <button
            data-testid="failed-marketplace-btn"
            onClick={() => navigate('/marketplace')}
            className="btn-primary"
          >
            Browse Movies
          </button>
        </>
      );
    }
    if (state.status === 'timeout') {
      return (
        <>
          <Clock size={84} weight="fill" className="text-slate-400 mx-auto mb-5" />
          <h1 className="font-heading text-3xl font-bold mb-3">
            STILL <span className="text-gold">PROCESSING</span>
          </h1>
          <p className="font-body text-slate-400 mb-6">{state.message}</p>
          <button
            data-testid="timeout-dashboard-btn"
            onClick={() => navigate(dashboardRoute())}
            className="btn-primary"
          >
            Go to Dashboard
          </button>
        </>
      );
    }
    return (
      <>
        <XCircle size={84} weight="fill" className="text-red-400 mx-auto mb-5" />
        <h1 className="font-heading text-3xl font-bold mb-3">SOMETHING <span className="text-red-400">WENT WRONG</span></h1>
        <p className="font-body text-slate-400 mb-6">{state.message}</p>
        <button
          data-testid="error-marketplace-btn"
          onClick={() => navigate('/marketplace')}
          className="btn-primary"
        >
          Browse Movies
        </button>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="noise-overlay" />
      <nav data-web-chrome="nav" className="relative z-10 glass-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            data-testid="success-home-btn"
            onClick={() => navigate('/')}
            className="flex items-center space-x-3"
          >
            <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
          </button>
        </div>
      </nav>
      <div className="relative flex-1 flex items-center justify-center px-4 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card border border-white/5 rounded-lg p-10 max-w-lg w-full text-center"
          data-testid="payment-success-card"
          role="status"
          aria-live="polite"
          aria-busy={state.status === 'polling'}
        >
          {renderBody()}
        </motion.div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
